import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';

const AdstraSettings = () => {
  const [settings, setSettings] = useState({
    adstraLink: '',
    impressionsRequired: 2,
    isEnabled: false,
    totalImpressions: 0,
    completedUsers: 0,
    dailyImpressions: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/adstra/settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching Adstra settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const token = localStorage.getItem('adminToken');
      const response = await axios.put(
        `${API_BASE_URL}/api/admin/adstra/settings`,
        {
          adstraLink: settings.adstraLink,
          impressionsRequired: settings.impressionsRequired,
          isEnabled: settings.isEnabled
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSettings(response.data);
      setSuccess('Settings saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleResetStats = async () => {
    if (!window.confirm('Are you sure you want to reset all Adstra statistics? This cannot be undone.')) {
      return;
    }
    
    try {
      setSaving(true);
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/adstra/reset-stats`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSettings(response.data.settings);
      setSuccess('Statistics reset successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error resetting stats:', err);
      setError('Failed to reset statistics');
    } finally {
      setSaving(false);
    }
  };

  // Get last 7 days impressions for chart
  const getLast7DaysData = () => {
    const last7Days = [];
    const today = new Date();
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toDateString();
      
      const found = settings.dailyImpressions?.find(
        d => new Date(d.date).toDateString() === dateStr
      );
      
      last7Days.push({
        date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        count: found?.count || 0
      });
    }
    
    return last7Days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const last7Days = getLast7DaysData();
  const maxImpressions = Math.max(...last7Days.map(d => d.count), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <span className="text-3xl">📢</span>
            Adstra Settings
          </h1>
          <p className="text-gray-500 mt-1">Manage advertisement links and impression settings</p>
        </div>
      </div>

      {/* Alert Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <span className="text-xl">⚠️</span>
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">✕</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <span className="text-xl">✅</span>
          {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">✕</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">👁️</span>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Total Impressions</p>
              <p className="text-2xl font-bold text-gray-900">{settings.totalImpressions?.toLocaleString() || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">✅</span>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Completed Users</p>
              <p className="text-2xl font-bold text-gray-900">{settings.completedUsers?.toLocaleString() || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <span className="text-2xl">🔢</span>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Impressions Required</p>
              <p className="text-2xl font-bold text-gray-900">{settings.impressionsRequired}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 ${settings.isEnabled ? 'bg-green-100' : 'bg-red-100'} rounded-xl flex items-center justify-center`}>
              <span className="text-2xl">{settings.isEnabled ? '🟢' : '🔴'}</span>
            </div>
            <div>
              <p className="text-gray-500 text-sm">Status</p>
              <p className={`text-xl font-bold ${settings.isEnabled ? 'text-green-600' : 'text-red-600'}`}>
                {settings.isEnabled ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
          <span>⚙️</span> Configuration
        </h2>
        
        <div className="space-y-5">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
            <div>
              <p className="font-semibold text-gray-800">Enable Adstra</p>
              <p className="text-sm text-gray-500">When enabled, users will see ads before accessing categories</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.isEnabled}
                onChange={(e) => setSettings({ ...settings, isEnabled: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Adstra Link */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Adstra Link URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              value={settings.adstraLink}
              onChange={(e) => setSettings({ ...settings, adstraLink: e.target.value })}
              placeholder="https://example.com/ad-link"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter the full URL where users will be redirected when clicking on categories
            </p>
          </div>

          {/* Impressions Required */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Impressions Required
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max="10"
                value={settings.impressionsRequired}
                onChange={(e) => setSettings({ ...settings, impressionsRequired: parseInt(e.target.value) })}
                className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
              />
              <span className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-xl font-bold text-blue-600">
                {settings.impressionsRequired}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Number of times a user must view the ad before accessing content. 
              <strong> 0 = No ads</strong>, users go directly to categories.
            </p>
          </div>

          {/* How it works */}
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
            <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <span>💡</span> How it works
            </h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• When a user clicks any category on the homepage, they'll be redirected to the Adstra link</li>
              <li>• When they return and click again, they'll see the ad again until reaching the required impressions</li>
              <li>• After {settings.impressionsRequired} impression{settings.impressionsRequired !== 1 ? 's' : ''}, the user can access the category directly</li>
              <li>• The counter resets when the browser session ends (closing the browser)</li>
            </ul>
          </div>

          {/* Save Button */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Saving...
                </>
              ) : (
                <>
                  💾 Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Daily Impressions Chart */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <span>📊</span> Last 7 Days Impressions
          </h2>
          <button
            onClick={handleResetStats}
            disabled={saving}
            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-all disabled:opacity-50"
          >
            🔄 Reset Stats
          </button>
        </div>
        
        <div className="flex items-end justify-between gap-2 h-40">
          {last7Days.map((day, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div className="w-full bg-gray-100 rounded-t-lg relative" style={{ height: '120px' }}>
                <div 
                  className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-blue-400 rounded-t-lg transition-all"
                  style={{ height: `${(day.count / maxImpressions) * 100}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                ></div>
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 text-xs font-bold text-gray-700">
                  {day.count > 0 ? day.count : ''}
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center whitespace-nowrap">{day.date}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Preview */}
      {settings.isEnabled && settings.adstraLink && (
        <div className="bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl p-6 border border-yellow-200">
          <h3 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
            <span className="text-xl">👀</span> Preview
          </h3>
          <div className="bg-white rounded-lg p-4 border border-yellow-100">
            <p className="text-sm text-gray-600 mb-2">
              When users click a category, they will be redirected to:
            </p>
            <a 
              href={settings.adstraLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 font-mono text-sm break-all"
            >
              {settings.adstraLink}
            </a>
            <p className="text-xs text-gray-500 mt-3">
              This will happen {settings.impressionsRequired} time{settings.impressionsRequired !== 1 ? 's' : ''} before they can access the actual category.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdstraSettings;
